import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

const categoryHeroImages: Record<string, string> = {
  romance: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20romantic%20love%20themed%20illustration%20as%20website%20banner%2C%20warm%20soft%20pink%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20artistic%20atmospheric%20background%2C%20cherry%20blossom%20petals%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-romance&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20magical%20fantasy%20themed%20illustration%20as%20website%20banner%2C%20warm%20golden%20magical%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20ancient%20temple%20ruins%2C%20mystical%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20action%20fighting%20themed%20illustration%20as%20website%20banner%2C%20warm%20dramatic%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20dynamic%20energy%20effects%2C%20intense%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-action&orientation=landscape',
  thriller: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20mystery%20suspense%20themed%20illustration%20as%20website%20banner%2C%20dark%20moody%20warm%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20noir%20detective%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-thriller&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20funny%20comedy%20school%20life%20themed%20illustration%20as%20website%20banner%2C%20bright%20cheerful%20warm%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20playful%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-comedy&orientation=landscape',
  drama: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20deep%20emotional%20drama%20themed%20illustration%20as%20website%20banner%2C%20warm%20cinematic%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20contemplative%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-drama&orientation=landscape',
  bl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20tender%20romantic%20BL%20themed%20illustration%20as%20website%20banner%2C%20soft%20warm%20dreamy%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20gentle%20romantic%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-bl&orientation=landscape',
  gl: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20gentle%20romantic%20GL%20themed%20illustration%20as%20website%20banner%2C%20soft%20warm%20silver%20moonlight%20lighting%2C%20stylized%20abstract%20comic%20panel%20elements%2C%20dreamy%20romantic%20atmosphere%2C%20clean%20minimal%20composition%2C%20high%20quality%20digital%20art&width=1920&height=800&seq=cat-hero-gl&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug]
  );

  const categoryComics = useMemo(
    () => comics.filter((c) => c.category === activeSlug),
    [activeSlug]
  );

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="w-full relative h-[280px] md:h-[380px] overflow-hidden">
          <img
            src={categoryHeroImages[activeSlug] || categoryHeroImages.romance}
            alt={`${currentCategory.name} - 韩漫分类`}
            title={`${currentCategory.name} - 韩漫在线免费阅读`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="w-full max-w-[1280px] mx-auto">
                <h1 className="text-3xl md:text-5xl font-bold text-background-50" style={{ fontFamily: 'var(--font-heading)' }}>
                  {currentCategory.name}
                </h1>
                <p className="mt-3 text-sm md:text-base text-background-50/80 max-w-lg">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <div className="w-full max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="w-full max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                {currentCategory.name}
              </h2>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 {categoryComics.length} 部作品
              </span>
            </div>

            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory.name} - 韩漫在线免费阅读`,
          description: `韩漫网${currentCategory.name}分类，收录${currentCategory.comicCount.toLocaleString()}部韩国漫画作品。${currentCategory.description}`,
          url: `http://ryjfh.cn/category/${activeSlug}`,
          isPartOf: { '@type': 'WebSite', name: '韩漫网', url: 'http://ryjfh.cn/' },
        })}
      </script>
    </>
  );
}